<script setup lang="ts">
  import { type Call } from '@bv/stores';
  import { sip } from '@bv/utils';

  import AnswerHangupAction from './actions/answer-and-hangup/AnswerHangupAction.vue';
  import AnswerHoldAction from './actions/answer-and-hold/AnswerHoldAction.vue';
  import HangupAction from './actions/hangup/HangupAction.vue';

  const props = defineProps<{
    call: Call;
    previousCall: Call;
  }>();

  /**
   * Accepts the current incoming call and immediately hangs up the previous call.
   * This is typically used in a "call waiting" scenario where the user chooses
   * to end the first call to answer the new one.
   * @returns {void}
   */
  const acceptAndHangup = (): void => {
    if (props.call) {
      sip.accept(props.call);
      sip.hangup(props.previousCall);
    }
  };

  /**
   * Hangs up the current call.
   * Passing 'true' as the second argument to `sip.hangup` signals that
   * the previously active call should be resumed if it exists.
   * @returns {void}
   */
  const hangup = (): void => {
    sip.hangup(props.call, true);
  };

  /**
   * Accepts the current incoming call and puts the previous call on hold.
   * This allows the user to answer a new call without disconnecting the existing one.
   * @returns {void}
   */
  const acceptAndHold = (): void => {
    sip.accept(props.call);
    sip.hold(props.previousCall);
  };
</script>

<template>
  <div class="flex justify-around gap-5 text-center text-xs leading-3 text-white">
    <answer-hangup-action @click="acceptAndHangup" />
    <hangup-action @click="hangup" />
    <answer-hold-action @click="acceptAndHold" />
  </div>
</template>
